// app/javascript/controllers/chat_partner_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "form",
    "messagesContainer",
    "messagesList",
    "emptyState",
    "textarea",
    "preview",
    "fileInput"
  ]

  autoScrollEnabled = true
  selectedFiles     = []
  placeholderId     = null
  conversationStarted = false

  /* ------------ ciclo de vida ------------ */
  connect() {
    this.handleTextareaInput()
    this.updateEmptyState()

    this.observer = new MutationObserver(() => {
      this.updateEmptyState()
      this.scrollToBottom()
    })
    this.observer.observe(this.messagesListTarget,{ childList: true, subtree: true })
    this.observer.observe(this.messagesContainerTarget, { childList: true, subtree: true })
    

    this.messagesContainerTarget.addEventListener("scroll",
      this.handleUserScroll.bind(this))
    this.previewTarget.addEventListener("click",
      this.removeFile.bind(this))
    document.addEventListener("turbo:before-stream-render", this.beforeStreamRender)

    this.scrollToBottom(true)
  }

  disconnect() {
    document.removeEventListener("turbo:before-stream-render", this.beforeStreamRender)
  }

  /* ------------ helpers UI ------------- */
  updateEmptyState() {
    const hasMessages = this.messagesListTarget.children.length > 0 || this.conversationStarted
    if (this.messagesListTarget.children.length > 0) this.conversationStarted = true
    this.setConversationMode(hasMessages)
  }

  setConversationMode(hasMessages) {
    this.element.classList.toggle("has-messages", hasMessages)
    this.element.classList.toggle("no-messages", !hasMessages)
    if (this.hasEmptyStateTarget) this.emptyStateTarget.hidden = hasMessages
  }

  scrollToBottom(force = false) {
    if (!this.autoScrollEnabled && !force) return
    requestAnimationFrame(() =>
      this.messagesContainerTarget.scrollTo(
        { top: this.messagesContainerTarget.scrollHeight })
    )
  }

  clearForm() {
    this.formTarget.reset()
    this.selectedFiles = []
    this.previewTarget.innerHTML = ""
    this.textareaTarget.style.height = null
    this.handleTextareaInput()
  }

  /* -------- placeholder “Subiendo…” ----- */
  insertPlaceholder() {
    if (this.placeholderId) return
    const id  = `ph-${Date.now()}`
    const wrapper = document.createElement("article")
    wrapper.id        = id
    wrapper.className = "message user"
    wrapper.innerHTML = `
      <div class="message-avatar avatar-user" aria-hidden="true">Tú</div>
      <div class="message-body">
        <div class="message-bubble">
          <div class="markdown-content">
            <span class="thinking">Subiendo archivo</span>
          </div>
        </div>
      </div>`
    this.messagesListTarget.appendChild(wrapper)
    this.placeholderId = id
    this.updateEmptyState()
    this.scrollToBottom(true)
  }

  removePlaceholder() {
    document.getElementById(this.placeholderId)?.remove()
    this.placeholderId = null
    this.updateEmptyState()
  }

  /* -------- envío del formulario -------- */
  beforeSubmit() {
    const hasFiles =
      this.selectedFiles.length > 0 ||      // adjuntos acumulados
      this.fileInputTarget.files.length > 0 // selección actual
    const hasText = this.textareaTarget.value.trim().length > 0

    if (hasText || hasFiles) {
      this.conversationStarted = true
      this.setConversationMode(true)
      this.autoScrollEnabled = true
    }

    if (hasFiles) {
      this.insertPlaceholder()
    }
    /* NO reseteamos el form aquí */
  }

  handleAfterSubmit(event) {
    this.removePlaceholder()
    this.scrollToBottom(true)
    this.clearForm()                  // ⬅️ ahora sí limpiamos la UI
  }

  // Descarta una reescritura de streaming tardía. El stream usa `update` (reescribe
  // el contenido completo, idempotente), así que esto ya NO evita duplicados —no
  // pueden ocurrir—; solo impide que un `update` rezagado pise el markdown final
  // una vez que el `replace` del frame marcó la burbuja como completa.
  beforeStreamRender = (event) => {
    const stream = event.target
    if (stream?.getAttribute("action") !== "update") return

    const target = stream.getAttribute("target")
    if (!target?.startsWith("content_message_")) return

    const content = document.getElementById(target)
    if (content?.dataset.chatStreamComplete === "true") event.preventDefault()
  }

  handleKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      this.formTarget.requestSubmit()   // dispara beforeSubmit
    }
  }

  useSuggestion(event) {
    const prompt = event.currentTarget.dataset.prompt
    if (!prompt) return

    this.textareaTarget.value = prompt
    this.handleTextareaInput()
    this.formTarget.requestSubmit()
  }

  /* -------- textarea -------------------- */
  handleTextareaInput() {
    this.textareaTarget.style.height = "auto"
    this.textareaTarget.style.height =
      `${this.textareaTarget.scrollHeight}px`
  }

  /* -------- adjuntos -------------------- */
  previewFiles(e) {
    const fresh = Array.from(e.target.files)

    fresh.forEach(f => {
      const dup = this.selectedFiles.some(
        s => s.name===f.name && s.size===f.size &&
             s.lastModified===f.lastModified && s.type===f.type)
      if (!dup) this.selectedFiles.push(f)
    })

    /* reflejar en input.files */
    const dt = new DataTransfer()
    this.selectedFiles.forEach(f => dt.items.add(f))
    this.fileInputTarget.files = dt.files

    this.renderPreviews()
  }

  renderPreviews() {
    this.previewTarget.innerHTML = ""
    this.selectedFiles.forEach((file, idx) => {
      const box = document.createElement("div")
      box.className   = "preview-item"
      box.dataset.index = idx

      if (file.type.startsWith("image/")) {
        const url = URL.createObjectURL(file)
        box.style.backgroundImage = `url(${url})`
        box.dataset.url = url
      } else {
        box.textContent = file.name.split(".").pop().toUpperCase()
      }

      const close = document.createElement("button")
      close.type  = "button"
      close.className = "preview-remove"
      close.textContent = "✕"
      close.setAttribute("aria-label","Eliminar adjunto")
      box.appendChild(close)

      this.previewTarget.appendChild(box)
    })
  }

  removeFile(e) {
    const btn = e.target.closest(".preview-remove")
    if (!btn) return
    const item = btn.closest(".preview-item")
    const idx  = +item.dataset.index
    const [removed] = this.selectedFiles.splice(idx, 1)
    if (removed?.type.startsWith("image/") && item.dataset.url) {
      URL.revokeObjectURL(item.dataset.url)
    }
    const dt = new DataTransfer()
    this.selectedFiles.forEach(f => dt.items.add(f))
    this.fileInputTarget.files = dt.files
    this.renderPreviews()
  }

  /* -------- scroll y hover -------------- */
  handleUserScroll() {
    const c = this.messagesContainerTarget
    this.autoScrollEnabled =
      c.scrollHeight - (c.scrollTop + c.clientHeight) < 100
  }
}
